import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Copy, Link2, Link2Off, MessageCircle, ShieldCheck } from "lucide-react";
import {
  formatApiError,
  telegramLinkInit,
  telegramPatchPrefs,
  telegramStatus,
  telegramUnlink,
  toastApiError,
} from "@/lib/api";

const CATEGORY_ROWS = [
  { key: "order_fills", label: "Order fills",
    hint: "Every filled buy / sell (Alpaca + sim)" },
  { key: "proposals", label: "New proposals",
    hint: "Verdict-generated proposals with approve/reject buttons" },
  { key: "auto_decisions", label: "Auto-mode decisions",
    hint: "Auto-executes and auto-rejects" },
  { key: "risk_alerts", label: "Risk alerts",
    hint: "Drawdown kill-switch breaches and auto-mode suspensions" },
  { key: "verdicts", label: "Analysis verdicts",
    hint: "Every completed analysis run" },
  { key: "daily_report", label: "Daily report",
    hint: "16:05 ET · per-portfolio equity + P&L + trade activity" },
  { key: "weekly_report", label: "Weekly report",
    hint: "Friday 16:15 ET · win rate + top winners/losers" },
  { key: "monthly_report", label: "Monthly report",
    hint: "Last calendar day 16:30 ET · full performance summary" },
];

export default function TelegramSettingsPage() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [linkPayload, setLinkPayload] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    try {
      setStatus(await telegramStatus());
    } catch (e) {
      toastApiError(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const startLink = async () => {
    setBusy(true);
    try {
      const res = await telegramLinkInit();
      setLinkPayload(res);
      toast.success("Link code ready — send it to the bot within 15 min.");
    } catch (e) {
      toastApiError(e);
    } finally {
      setBusy(false);
    }
  };

  const unlink = async () => {
    if (!window.confirm(
      "Unlink Telegram? You'll stop receiving notifications and won't be "
      + "able to approve proposals from the bot. Your account and portfolios "
      + "are unaffected.",
    )) return;
    setBusy(true);
    try {
      await telegramUnlink();
      setLinkPayload(null);
      toast.success("Telegram unlinked.");
      await refresh();
    } catch (e) {
      toastApiError(e);
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (key, val) => {
    try {
      const updated = await telegramPatchPrefs({ [key]: val });
      setStatus((s) => (s ? { ...s, prefs: updated } : s));
    } catch (e) {
      toastApiError(e);
    }
  };

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied");
    } catch {
      toast.error("Copy failed — select the code and copy manually.");
    }
  };

  if (loading) {
    return (
      <div className="py-16 text-center text-sm text-[var(--aurion-muted)]"
           data-testid="telegram-settings-loading">
        Loading Telegram status…
      </div>
    );
  }

  if (!status?.enabled) {
    return (
      <div className="mx-auto max-w-3xl space-y-6" data-testid="telegram-settings-page">
        <header>
          <h1 className="font-brand text-3xl text-[var(--aurion-gold)]">
            Telegram
          </h1>
        </header>
        <Alert>
          <AlertTitle>Telegram bot not configured</AlertTitle>
          <AlertDescription>
            The server hasn't set <code>TELEGRAM_BOT_TOKEN</code>. Contact the
            operator to enable this feature.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const bot = status.bot_username;
  const linked = !!status.linked;
  const prefs = status.prefs || {};

  return (
    <div className="mx-auto max-w-3xl space-y-6" data-testid="telegram-settings-page">
      <header>
        <h1 className="font-brand text-3xl text-[var(--aurion-gold)]">
          Telegram
        </h1>
        <p className="mt-1 text-sm text-[var(--aurion-muted)]">
          Real-time fills, proposal buttons, and daily/weekly/monthly reports
          delivered to your phone via the bot{" "}
          <code>@{bot}</code>.
        </p>
      </header>

      <Alert
        data-testid="telegram-security-note"
        className="border-[var(--aurion-border)] bg-[var(--aurion-panel)]/40"
      >
        <ShieldCheck className="h-4 w-4" />
        <AlertTitle>Your token stays server-side</AlertTitle>
        <AlertDescription className="text-xs text-[var(--aurion-muted)]">
          The bot token lives only in the server environment and is redacted
          from logs, LLM prompts, and any API response. Callback approvals
          run the same code path as the web (ownership + audit enforced).
        </AlertDescription>
      </Alert>

      {linked ? (
        <Card
          data-testid="telegram-linked-card"
          className="border-emerald-500/30 bg-[var(--aurion-panel)]/60"
        >
          <CardHeader className="flex flex-row items-center gap-3">
            <MessageCircle className="h-5 w-5 text-emerald-400" />
            <CardTitle className="text-base uppercase tracking-widest text-emerald-300">
              Connected
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Field label="Telegram username"
                   value={status.telegram_username ? `@${status.telegram_username}` : "—"}
                   testid="telegram-username" />
            <Field label="Chat ID"
                   value={String(status.chat_id ?? "—")}
                   testid="telegram-chat-id" />
            <Field label="Linked at"
                   value={status.linked_at
                     ? new Date(status.linked_at).toLocaleString()
                     : "—"}
                   testid="telegram-linked-at" />
            <div className="pt-2">
              <Button
                data-testid="telegram-unlink-btn"
                type="button"
                variant="outline"
                onClick={unlink}
                disabled={busy}
                className="border-[var(--aurion-red)]/40 text-[var(--aurion-red)] hover:bg-[var(--aurion-red)]/10"
              >
                <Link2Off className="mr-2 h-4 w-4" />
                Unlink Telegram
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : linkPayload ? (
        <Card
          data-testid="telegram-code-card"
          className="border-[var(--aurion-gold)]/40 bg-[var(--aurion-panel)]/60"
        >
          <CardHeader>
            <CardTitle className="text-base uppercase tracking-widest text-[var(--aurion-gold)]">
              One-time link code — expires in 15 min
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <ol className="list-decimal space-y-2 pl-5 text-sm">
              <li>
                Open{" "}
                <a
                  data-testid="telegram-deep-link"
                  href={linkPayload.deep_link}
                  target="_blank"
                  rel="noreferrer"
                  className="underline underline-offset-2"
                >
                  t.me/{bot}
                </a>
                {" "}on your phone.
              </li>
              <li>
                In the chat, tap <b>Start</b> — the bot will receive the code
                automatically. If it doesn't (some clients strip the query),
                send <code>/start {linkPayload.code}</code> manually.
              </li>
            </ol>
            <div className="flex items-center justify-between border border-[var(--aurion-border)] bg-[var(--aurion-panel-2)] px-3 py-2">
              <span
                data-testid="telegram-link-code"
                className="font-mono text-lg tracking-widest text-[var(--aurion-gold)]"
              >
                {linkPayload.code}
              </span>
              <Button
                data-testid="telegram-copy-btn"
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => copy(linkPayload.code)}
              >
                <Copy className="mr-1 h-3 w-3" />
                Copy
              </Button>
            </div>
            <p className="text-xs text-[var(--aurion-muted)]">
              Refresh this page after linking to see the connected badge.
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card
          data-testid="telegram-connect-card"
          className="border-[var(--aurion-border)] bg-[var(--aurion-panel)]/40"
        >
          <CardHeader className="flex flex-row items-center gap-3">
            <Link2 className="h-5 w-5 text-[var(--aurion-gold)]" />
            <CardTitle className="text-base uppercase tracking-widest text-[var(--aurion-gold)]">
              Link Telegram
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-[var(--aurion-muted)]">
              Generate a one-time code and paste it into the bot. Linking is
              exclusive — one Telegram chat per account, one account per chat.
            </p>
            <div className="pt-4">
              <Button
                data-testid="telegram-link-btn"
                type="button"
                onClick={startLink}
                disabled={busy}
                className="bg-[var(--aurion-gold)] text-black hover:bg-[var(--aurion-gold)]/90"
              >
                {busy ? "Generating…" : "Generate link code"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card
        data-testid="telegram-prefs-card"
        className="border-[var(--aurion-border)] bg-[var(--aurion-panel)]/40"
      >
        <CardHeader>
          <CardTitle className="text-base uppercase tracking-widest text-[var(--aurion-gold)]">
            Notifications
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-1">
          {CATEGORY_ROWS.map((row) => (
            <div
              key={row.key}
              data-testid={`telegram-pref-${row.key}`}
              className="flex items-start justify-between gap-6 border-b border-[var(--aurion-border)] py-3 last:border-b-0"
            >
              <div className="flex-1">
                <div className="text-sm text-[var(--aurion-text)]">
                  {row.label}
                </div>
                <div className="text-xs text-[var(--aurion-muted)]">
                  {row.hint}
                </div>
              </div>
              <Switch
                data-testid={`telegram-pref-${row.key}-toggle`}
                checked={!!prefs[row.key]}
                disabled={!linked}
                onCheckedChange={(val) => toggle(row.key, val)}
              />
            </div>
          ))}
          {!linked ? (
            <p className="pt-3 text-xs text-[var(--aurion-muted)]">
              Toggles are live once you link a Telegram chat.
            </p>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}

function Field({ label, value, testid }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-[var(--aurion-muted)]">
        {label}
      </div>
      <div
        data-testid={testid}
        className="mt-1 font-mono text-sm text-[var(--aurion-text)]"
      >
        {value}
      </div>
    </div>
  );
}
